import { useEffect, useRef, useState, useCallback } from 'react';
import { Plus } from 'lucide-react';

/**
 * PromptAccionesEditor — Editor del prompt del agente/campaña con selector de ACCIONES.
 *
 * El admin escribe en lenguaje natural y, donde quiera que la IA ejecute una acción, la inserta
 * desde "Insertar acción" (o pulsando una del catálogo). La acción se ve como un chip legible
 * dentro del prompt — nunca la sintaxis técnica [TAG]. Insertar una acción también la enciende
 * en active_skills. El valor que sube a onChange es el prompt con tokens {{accion:KEY}}; el backend
 * los traduce al comando real (core/ai_prompt_builder.py, mapa nombre→tag).
 *
 * Reusable por el Agente Principal (SettingsPage: system_prompt_override) y por cada Campaña IA
 * (AICampaignIndependentPage: behavior_prompt).
 *
 * Props:
 *   value           string — prompt con tokens {{accion:KEY}}
 *   onChange(str)   notifica el prompt serializado
 *   skills          [{ key, label, description }] — catálogo /ai/skills-catalog (ya filtrado por vertical)
 *   activeSkills    [key] — habilidades encendidas
 *   onToggleSkill(key, on) — enciende/apaga una habilidad
 *   iconFor(key)    (opcional) devuelve un componente de icono lucide para la key
 *   placeholder     string
 */

const TOKEN_RE = /\{\{accion:([a-z_]+)\}\}/g;

function escapeHtml(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Inyecta los estilos del editor una sola vez (clases lc-*, fuera del purge de Tailwind).
function ensureStyles() {
  if (typeof document === 'undefined' || document.getElementById('lc-prompt-acciones-css')) return;
  const el = document.createElement('style');
  el.id = 'lc-prompt-acciones-css';
  el.textContent = `
  .lc-ed{min-height:150px;border-radius:12px;padding:14px;font-size:14px;line-height:1.7;
    color:#EAF0F7;background:rgba(0,0,0,.25);border:1px solid rgba(255,255,255,.12);outline:none;
    white-space:pre-wrap;overflow-wrap:anywhere}
  .lc-ed:focus{border-color:#B8E400}
  .lc-ed:empty:before{content:attr(data-ph);color:#6B7E95}
  .lc-chip{display:inline-flex;align-items:center;gap:6px;background:rgba(184,228,0,.14);
    border:1px solid rgba(184,228,0,.5);color:#CDEE4A;border-radius:999px;padding:1px 6px 1px 9px;
    font-size:13px;font-weight:700;white-space:nowrap;margin:0 1px;user-select:none;vertical-align:baseline}
  .lc-chip-dot{width:7px;height:7px;border-radius:50%;background:#B8E400}
  .lc-chip-x{border:0;background:none;color:#CDEE4A;cursor:pointer;font-size:14px;line-height:1;
    padding:1px 2px;border-radius:50%;opacity:.7}
  .lc-chip-x:hover{opacity:1;background:rgba(255,255,255,.1)}
  .lc-ins{display:inline-flex;align-items:center;gap:7px;background:rgba(255,255,255,.06);color:#EAF0F7;
    border:1px dashed rgba(255,255,255,.25);border-radius:999px;padding:6px 13px;font-weight:700;
    font-size:13px;cursor:pointer}
  .lc-ins:hover{border-color:#B8E400;color:#CDEE4A}
  .lc-menu{border:1px solid rgba(255,255,255,.14);background:#16263F;border-radius:12px;padding:6px;
    margin-top:8px;max-height:280px;overflow:auto}
  .lc-mi{display:flex;align-items:flex-start;gap:10px;width:100%;text-align:left;background:none;border:0;
    color:#EAF0F7;padding:8px;border-radius:8px;cursor:pointer;font-family:inherit}
  .lc-mi:hover{background:rgba(255,255,255,.07)}
  .lc-mi .lc-nm{font-weight:600;font-size:13.5px}
  .lc-mi .lc-ds{font-size:12px;color:#93A6BD;margin-top:1px}
  .lc-mi .lc-add{margin-left:auto;color:#B8E400;font-weight:800;font-size:16px;line-height:1.3}
  .lc-mi.lc-on{opacity:.5}
  .lc-peek{background:none;border:0;color:#93A6BD;font-size:12.5px;cursor:pointer;padding:0;margin-top:8px}
  .lc-peek:hover{color:#EAF0F7}
  .lc-compiled{margin-top:8px;background:#07101d;border:1px solid rgba(255,255,255,.1);border-radius:10px;
    padding:12px;font-family:ui-monospace,Menlo,monospace;font-size:12px;color:#93A6BD;white-space:pre-wrap;
    overflow-wrap:anywhere;line-height:1.7}
  .lc-compiled .lc-tg{color:#CDEE4A}`;
  document.head.appendChild(el);
}

export default function PromptAccionesEditor({
  value = '',
  onChange,
  skills = [],
  activeSkills = [],
  onToggleSkill,
  placeholder = 'Describe cómo debe actuar tu agente…',
}) {
  const ref = useRef(null);
  const savedRange = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [peek, setPeek] = useState(false);
  const labelByKey = {};
  skills.forEach((s) => { labelByKey[s.key] = s.label || s.key; });

  const chipHTML = useCallback((key) => {
    const label = labelByKey[key] || key;
    return `<span class="lc-chip" contenteditable="false" data-key="${key}">`
      + `<span class="lc-chip-dot"></span>${escapeHtml(label)}`
      + `<button type="button" class="lc-chip-x" tabindex="-1" aria-label="Quitar acción">×</button></span>`;
  }, [skills]); // eslint-disable-line react-hooks/exhaustive-deps

  const valueToHtml = useCallback((val) => {
    let html = '';
    let last = 0;
    const src = String(val || '');
    src.replace(TOKEN_RE, (m, key, idx) => {
      html += escapeHtml(src.slice(last, idx)).replace(/\n/g, '<br>');
      html += chipHTML(key);
      last = idx + m.length;
      return m;
    });
    html += escapeHtml(src.slice(last)).replace(/\n/g, '<br>');
    return html;
  }, [chipHTML]);

  // Serializa el DOM del editor a texto con tokens {{accion:KEY}}.
  const serialize = useCallback(() => {
    const el = ref.current;
    if (!el) return '';
    let out = '';
    const walk = (node) => {
      node.childNodes.forEach((n) => {
        if (n.nodeType === 3) out += n.textContent;
        else if (n.nodeType === 1 && n.classList.contains('lc-chip')) out += `{{accion:${n.dataset.key}}}`;
        else if (n.nodeType === 1 && n.tagName === 'BR') out += '\n';
        else if (n.nodeType === 1) { walk(n); if (/^(DIV|P)$/.test(n.tagName)) out += '\n'; }
      });
    };
    walk(el);
    return out.replace(/\n{3,}/g, '\n\n');
  }, []);

  const emit = useCallback(() => { if (onChange) onChange(serialize()); }, [onChange, serialize]);

  // Inicializa el contenido UNA vez (el contenteditable no lo controla React).
  useEffect(() => {
    ensureStyles();
    if (ref.current) ref.current.innerHTML = valueToHtml(value);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rememberCaret = () => {
    const s = window.getSelection();
    if (s && s.rangeCount && ref.current && ref.current.contains(s.anchorNode)) {
      savedRange.current = s.getRangeAt(0).cloneRange();
    }
  };

  const insert = (key) => {
    const el = ref.current;
    if (!el) return;
    if (el.querySelector(`.lc-chip[data-key="${key}"]`)) { setMenuOpen(false); return; }
    const tmp = document.createElement('div');
    tmp.innerHTML = chipHTML(key);
    const chip = tmp.firstChild;
    const space = document.createTextNode(' ');
    const r = (savedRange.current && el.contains(savedRange.current.startContainer)) ? savedRange.current : null;
    if (r) { r.collapse(false); r.insertNode(space); r.insertNode(chip); }
    else { el.appendChild(document.createTextNode(' ')); el.appendChild(chip); el.appendChild(space); }
    if (onToggleSkill && !activeSkills.includes(key)) onToggleSkill(key, true);
    setMenuOpen(false);
    emit();
  };

  const onEditorClick = (e) => {
    const x = e.target.closest('.lc-chip-x');
    if (!x) return;
    const chip = x.closest('.lc-chip');
    if (chip) chip.remove();
    emit();
  };

  const compiledText = () => {
    const el = ref.current;
    if (!el) return '';
    let out = '';
    el.childNodes.forEach((n) => {
      if (n.nodeType === 3) out += escapeHtml(n.textContent);
      else if (n.nodeType === 1 && n.classList.contains('lc-chip')) out += `<span class="lc-tg">[${n.dataset.key.toUpperCase()}]</span>`;
      else if (n.nodeType === 1 && n.tagName === 'BR') out += '\n';
      else if (n.nodeType === 1) out += escapeHtml(n.textContent);
    });
    return out;
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '8px' }}>
        <button type="button" className="lc-ins" onClick={() => setMenuOpen((o) => !o)} aria-expanded={menuOpen}>
          <Plus className="w-3.5 h-3.5" style={{ color: '#B8E400' }} /> Insertar acción
        </button>
        <span style={{ fontSize: '12px', color: '#6B7E95' }}>elige la acción; el sistema la conecta por ti</span>
      </div>

      {menuOpen && (
        <div className="lc-menu" role="listbox">
          {skills.length === 0 && (
            <div style={{ padding: '10px', fontSize: '13px', color: '#93A6BD' }}>No hay acciones disponibles para esta vertical.</div>
          )}
          {skills.map((s) => {
            const on = activeSkills.includes(s.key);
            return (
              <button type="button" key={s.key} className={`lc-mi${on ? ' lc-on' : ''}`} onClick={() => insert(s.key)}>
                <span style={{ minWidth: 0 }}>
                  <span className="lc-nm">{s.label || s.key}</span>
                  <span className="lc-ds">{s.description || ''}</span>
                </span>
                <span className="lc-add">+</span>
              </button>
            );
          })}
        </div>
      )}

      <div
        ref={ref}
        className="lc-ed"
        contentEditable
        suppressContentEditableWarning
        data-ph={placeholder}
        spellCheck={false}
        onInput={emit}
        onBlur={emit}
        onClick={onEditorClick}
        onKeyUp={rememberCaret}
        onMouseUp={rememberCaret}
        role="textbox"
        aria-multiline="true"
        aria-label="Prompt de comportamiento"
      />

      <button type="button" className="lc-peek" onClick={() => setPeek((p) => !p)} aria-pressed={peek}>
        {peek ? 'Ocultar' : 'Ver cómo lo entiende el sistema'} (comandos técnicos)
      </button>
      {peek && (
        <pre className="lc-compiled" dangerouslySetInnerHTML={{ __html: compiledText() }} />
      )}
    </div>
  );
}
